"use client";

import { useReportWebVitals } from "next/web-vitals";

import { getCatalogSessionId } from "@/components/catalog/analytics";
import { webVitalMetricNames, type WebVital } from "@/domain/analytics/web-vital";

const reportedRatings = new Map<string, number>();

export function shouldReportWebVital(metric: Pick<WebVital, "id" | "name" | "rating" | "sessionId">): boolean {
  const key = `${metric.name}:${metric.id}`;
  const severity = metric.rating === "poor" ? 2 : metric.rating === "needs-improvement" ? 1 : 0;
  if ((reportedRatings.get(key) ?? -1) >= severity) return false;
  if (severity > 0) {
    reportedRatings.set(key, severity);
    return true;
  }
  // Stable 10% sample across navigations; poor vitals are always retained.
  const hash = Array.from(metric.sessionId).reduce((value, character) =>
    (value * 31 + character.charCodeAt(0)) >>> 0, 0);
  if (hash % 10 !== 0) return false;
  reportedRatings.set(key, severity);
  return true;
}

export function WebVitalsReporter() {
  useReportWebVitals((metric) => {
    if (!isSupportedMetricName(metric.name)) {
      return;
    }

    const connection = (navigator as Navigator & {
      connection?: { effectiveType?: string };
    }).connection;
    const payload: WebVital = {
      effectiveConnectionType: connection?.effectiveType,
      id: metric.id,
      name: metric.name,
      navigationType: metric.navigationType,
      path: window.location.pathname,
      rating: metric.rating,
      sessionId: getCatalogSessionId(),
      value: metric.value,
    };

    if (!shouldReportWebVital(payload)) return;

    void fetch("/api/analytics/web-vitals", {
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      method: "POST",
    });
  });

  return null;
}

function isSupportedMetricName(
  name: string,
): name is WebVital["name"] {
  return webVitalMetricNames.some((metricName) => metricName === name);
}
